import React, { useEffect, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Chip, Tab, Tabs, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getLegalPacks } from '../../Api/AiModels';

/**
 * Read-only view of the Policy Reviewer legal packs. Sign-off is shown.
 * The method text cannot be edited from Trinity.
 */
const signOff = (item) => (item?.reviewed_by && item?.reviewed_on
  ? `Signed off by ${item.reviewed_by}, ${item.reviewed_on}`
  : 'Not signed off');

const MethodText = ({ text }) => (
  <Box
    component="pre"
    sx={{
      m: 0,
      whiteSpace: 'pre-wrap',
      fontFamily: 'inherit',
      fontSize: 14,
      lineHeight: 1.6,
      color: '#111827',
    }}
  >
    {text}
  </Box>
);

const TrinityLegalPacks = ({ load = getLegalPacks }) => {
  const [packs, setPacks] = useState([]);
  const [experts, setExperts] = useState([]);
  const [tab, setTab] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    load()
      .then((res) => {
        if (!alive) return;
        setPacks(Array.isArray(res?.data?.packs) ? res.data.packs : []);
        setExperts(Array.isArray(res?.data?.experts) ? res.data.experts : []);
        setError('');
      })
      .catch((err) => {
        if (!alive) return;
        setError(err?.response?.status === 403 ? 'Tech Admin only' : 'Could not load the legal packs');
      });
    return () => { alive = false; };
  }, [load]);

  const pack = packs[tab];
  const signed = pack?.reviewed_by && pack?.reviewed_on;

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827' }}>Legal packs</Typography>
      <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5, mb: 2 }}>
        The Singapore and Australia methods, and the Zavvy expert methods. Read only.
      </Typography>
      {error ? <Alert severity="error">{error}</Alert> : null}
      {packs.length ? (
        <>
          <Tabs value={tab} onChange={(_, next) => setTab(next)} sx={{ mb: 2 }}>
            {packs.map((item) => (
              <Tab key={item.slug} label={item.country} />
            ))}
          </Tabs>
          {pack ? (
            <Box>
              <Chip
                label={signOff(pack)}
                sx={{
                  mb: 2,
                  bgcolor: signed ? '#ECFDF5' : '#FEF2F2',
                  color: signed ? '#065F46' : '#991B1B',
                  fontWeight: 600,
                }}
              />
              {pack.documents.map((doc) => (
                <Accordion key={doc.name} disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 600 }}>{doc.title}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <MethodText text={doc.text} />
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          ) : null}
        </>
      ) : null}
      {experts.length ? (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Experts</Typography>
          {experts.map((expert) => (
            <Accordion key={expert.slug} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontWeight: 600 }}>{expert.label}</Typography>
                  <Chip size="small" label={signOff(expert)} />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <MethodText text={expert.text} />
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      ) : null}
    </Box>
  );
};

export default TrinityLegalPacks;
